// Ablage fuer Such-Embeddings in IndexedDB.
//
// Warum nicht localStorage (wo sie bis 23.08.2026 lagen): ein Embedding
// ist ein Float32Array mit 1024 Werten. Als JSON-Array sind das ~12-20 KB
// je Eintrag, und localStorage zaehlt UTF-16, also doppelt. Bei ~200
// indizierten Entitaeten plus den Summary-Snapshots derselben Quota war
// das Fach voll. Der alte Code fing QuotaExceeded ab, indem er den
// GESAMTEN Embedding-Cache wegwarf und einen Eintrag schrieb -- der
// naechste Start fand nichts vor und liess alles neu rechnen. Gemessen:
// 2,2 MB Embed-Verkehr bei JEDEM Boot, kalt wie warm.
//
// IndexedDB kennt diese Enge nicht und speichert Float32Array direkt
// (structured clone) -- kein JSON-Umweg, kein Parsen beim Lesen.
//
// Die Schreibvorgaenge laufen absichtlich beiseite (fire-and-forget,
// gebuendelt): der Suchpfad wartet nie auf die Platte. Faellt die
// Ablage komplett aus (privates Fenster, gesperrte Speicherung), ist
// das kein Fehlerfall -- die Suche rechnet dann eben pro Sitzung neu.

const DB_NAME = "homelab-dash-embeddings";
const DB_VERSION = 1;
const STORE = "vectors";

// Alter localStorage-Cache, der beim ersten Start uebernommen wird.
const LEGACY_PREFIX = "emb2:";

let _dbPromise: Promise<IDBDatabase | null> | null = null;

function openDB(): Promise<IDBDatabase | null> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      // Ein blockierter Upgrade (zweiter Tab haelt die alte Version)
      // darf den Start nicht anhalten -- dann eben ohne Ablage.
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return _dbPromise;
}

/**
 * Laedt den gesamten Cache in eine Map. Ein Zugriff beim Start ist
 * billiger als ein Lookup je Eintrag, und die Map ist danach die
 * synchrone Nachschlage-Adresse fuer den Suchpfad.
 */
export async function loadAllEmbeddings(): Promise<Map<string, Float32Array>> {
  const out = new Map<string, Float32Array>();
  const db = await openDB();
  if (!db) return out;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");
      const store = tx.objectStore(STORE);
      const req = store.openCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) {
          resolve();
          return;
        }
        const val = cur.value;
        if (val instanceof Float32Array) out.set(String(cur.key), val);
        else if (val instanceof ArrayBuffer) out.set(String(cur.key), new Float32Array(val));
        cur.continue();
      };
      req.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
  return out;
}

// Schreib-Puffer: Embeddings kommen in Schueben von 16 herein. Sie
// einzeln zu schreiben waere eine Transaktion je Vektor.
let _pending = new Map<string, Float32Array>();
let _flushTimer: ReturnType<typeof setTimeout> | undefined;

export function saveEmbedding(key: string, emb: Float32Array): void {
  _pending.set(key, emb);
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = undefined;
    void flushEmbeddings();
  }, 250);
}

export async function flushEmbeddings(): Promise<void> {
  if (_pending.size === 0) return;
  const batch = _pending;
  _pending = new Map();
  const db = await openDB();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      for (const [k, v] of batch) store.put(v, k);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

/**
 * Uebernimmt den alten localStorage-Cache einmalig und raeumt ihn weg.
 * Ohne das muesste der erste Start nach dem Umbau alles neu rechnen --
 * und der freigewordene Platz ist genau der, der den Snapshots der
 * Summary-Stores gefehlt hat.
 *
 * Rueckgabe: die uebernommenen Eintraege, damit der Aufrufer sie sofort
 * benutzen kann, ohne auf den Schreibvorgang zu warten.
 */
export function migrateLegacyEmbeddings(): Map<string, Float32Array> {
  const out = new Map<string, Float32Array>();
  let keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LEGACY_PREFIX)) keys.push(k);
    }
  } catch {
    return out;
  }
  for (const k of keys) {
    try {
      const raw = localStorage.getItem(k);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const emb = new Float32Array(arr);
          // Ohne den Prefix ablegen -- in IndexedDB gibt es keinen
          // Namensraum zu trennen, der Modellname steckt schon im Key.
          const key = k.slice(LEGACY_PREFIX.length);
          out.set(key, emb);
          saveEmbedding(key, emb);
        }
      }
    } catch {
      // Kaputter Eintrag: faellt unten mit weg.
    }
    try {
      localStorage.removeItem(k);
    } catch {
      /* Speicher gesperrt -- dann bleibt er halt liegen. */
    }
  }
  return out;
}

/**
 * Wirft alles weg, was nicht zum aktiven Modell gehoert. Die Keys sind
 * "<modell>:<text>" -- ein Modellwechsel entwertet die alten Vektoren
 * vollstaendig, sie waeren sonst totes Gewicht.
 */
export async function purgeOtherModels(model: string): Promise<void> {
  const db = await openDB();
  if (!db) return;
  const prefix = model + ":";
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const req = store.openCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) {
          resolve();
          return;
        }
        if (!String(cur.key).startsWith(prefix)) cur.delete();
        cur.continue();
      };
      req.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}
